import { type Handle } from 'remix/component'
import { createMultiMatcher } from 'remix/route-pattern/match'
import { type AppLoaderData } from '#universal/loader-data.ts'
import { isOnboardingPagePath } from '#universal/onboarding-process.ts'
import { isSamePackageRepoChromeHref } from '#universal/package-files.ts'
import { isProfilePathname } from '#universal/profile-path.ts'
import { isProfilePackageFilterOnlyHrefChange } from '#universal/profile-search.ts'
import { clearOnboardingPayloadCache } from '#client/routes/onboarding-payload.ts'
import { applyDocumentHead } from './document-head.ts'
import { installFileDropNavigationGuard } from './file-drop-navigation.ts'
import { syncPasswordManagerPageIgnore } from './password-manager-page-ignore.ts'
import {
	abortIntentPrefetch,
	prefetchEachRouteOnRender,
	prefetchRouteOnIntent,
	prefetchRoutesOnRender,
	takePrefetchedRouteResult,
} from './intent-prefetch.ts'
import {
	isClientRouteModuleCached,
	listenToLazyRouteLoads,
	preloadClientRouteModules,
} from './lazy-route.tsx'
import {
	markNavigationDataStale,
	setPreloadedNavigationData,
} from './navigation-data.ts'
import { isRouteLoaderRedirect, type RouteLoader } from './route-loader.ts'
import {
	readRouterPathname,
	readRouterUrl,
	readSsrRouterUrl,
} from './router-location.tsx'
import {
	createScrollRestorationHistoryState,
	ensureCurrentScrollRestorationKey,
	type RouterHistoryAction,
	type RouterNavigateOptions,
	type RouterNavigationEventDetail,
} from './router-scroll-state.ts'
import {
	createViewTransitionScheduler,
	type ViewTransitionStarter,
} from './view-transition-scheduler.ts'

export { type RouteLoader } from './route-loader.ts'

type RouterSetup = {
	routes: Record<string, JSX.Element>
	fallback?: JSX.Element
	loaderData?: AppLoaderData
	notFound?: boolean
	unauthorized?: boolean
	unauthorizedFallback?: JSX.Element
	internalError?: boolean
	internalErrorFallback?: JSX.Element
}

type FormMethod = 'get' | 'post'

type FormSubmitDetails = {
	action: URL
	method: FormMethod
	enctype: string
	formData: FormData
	preventScrollReset: boolean
}

type NavigationRunOptions = {
	/** Browser already changed the URL (popstate / same-path refresh). */
	skipPushState?: boolean
	/** Form POST already dispatched `navigationstart`; loader owns `navigationend`. */
	suppressStart?: boolean
	historyAction?: RouterHistoryAction
} & RouterNavigateOptions

const clientRouteOrigin = 'https://kody.local'
export const navigationTimeoutMs = 10_000
const routeMatchers = new WeakMap<
	Record<string, JSX.Element>,
	ReturnType<typeof createRouteMatcher>
>()
const loaderMatchers = new WeakMap<
	Record<string, RouteLoader>,
	ReturnType<typeof createLoaderMatcher>
>()
export const routerEvents = new EventTarget()
let routerInitialized = false
let registeredRouteLoaders: Record<string, RouteLoader> = {}
let navigationAbortController: AbortController | null = null
let activeNavigationPath: string | null = null
// The pathname+search (no hash) the app last rendered. Popstate compares
// against this to detect hash-only history moves, since `window.location`
// has already changed by the time the event fires.
let lastNotifiedDocumentPath: string | null =
	typeof window === 'undefined'
		? null
		: `${window.location.pathname}${window.location.search}`

function getCurrentDocumentPath() {
	return `${window.location.pathname}${window.location.search}`
}

/** Call after a `replaceState` that is not a router navigation. */
export function syncLastNotifiedDocumentPath() {
	if (typeof window === 'undefined') return
	lastNotifiedDocumentPath = getCurrentDocumentPath()
}

/**
 * Areas whose pages sit inside a persistent shell (a sticky rail beside the
 * content). Moving between them is tab switching, not page-to-page
 * navigation: it is frequent, the chrome around the content does not change,
 * and the rail is a full-height element whose box tracks the page — so a
 * transition would scale its snapshot between two different heights and the
 * rail would visibly squash. These navigations swap instantly.
 *
 * `/docs` is the same kind of shell: the sidebar lives inside `<main>`
 * (`view-transition-name: page`), so a page transition would fade and slide
 * unchanged nav chrome on every guide click.
 */
const shellAreas = ['/account', '/admin', '/docs']

/**
 * Live persistent-shell chrome. Present only while a shell page is on
 * screen — account/admin rail or the docs sidebar.
 */
export const persistentShellNavSelector = '[data-account-nav], [data-docs-nav]'

function isWithinArea(pathname: string, area: string) {
	return pathname === area || pathname.startsWith(`${area}/`)
}

function pathnameOf(path: string) {
	return path.split('?')[0] ?? ''
}

function isShellAreaPath(path: string) {
	const pathname = pathnameOf(path)
	return shellAreas.some((area) => isWithinArea(pathname, area))
}

/** Both paths may carry a search string; only the pathname decides the area. */
export function isSameShellAreaNavigation(from: string | null, to: string) {
	if (!from) return false
	return shellAreas.some(
		(area) =>
			isWithinArea(pathnameOf(from), area) &&
			isWithinArea(pathnameOf(to), area),
	)
}

function isSameOnboardingNavigation(from: string | null, to: string) {
	if (!from) return isOnboardingPagePath(pathnameOf(to))
	return (
		isOnboardingPagePath(pathnameOf(from)) &&
		isOnboardingPagePath(pathnameOf(to))
	)
}

function isSameProfilePathnameNavigation(from: string | null, to: string) {
	if (from == null) return false
	const fromPathname = pathnameOf(from)
	return fromPathname === pathnameOf(to) && isProfilePathname(fromPathname)
}

export function documentHasPersistentShell(
	root: {
		querySelector: (selector: string) => Element | null
	} | null = typeof document === 'undefined' ? null : document,
) {
	return Boolean(root?.querySelector(persistentShellNavSelector))
}

/**
 * View transitions are for real page changes. Tab switches inside a
 * persistent shell stay instant — even when `from` was never recorded
 * (first click after a full document load used to animate because
 * `lastNotifiedDocumentPath` was still null).
 */
export function shouldUseViewTransition(input: {
	from: string | null
	to: string
	canStart: boolean
	prefersReducedMotion: boolean
	hasPersistentShell?: boolean
}) {
	if (!input.canStart) return false
	if (input.prefersReducedMotion) return false
	if (input.from === input.to) return false
	if (isSameShellAreaNavigation(input.from, input.to)) return false
	if (isSameOnboardingNavigation(input.from, input.to)) return false
	if (isSameProfilePathnameNavigation(input.from, input.to)) return false
	if (input.from != null && isSamePackageRepoChromeHref(input.from, input.to)) {
		return false
	}
	// Full document load never recorded `from`. If the rail is already on
	// screen and the destination is still a shell page, this is a tab click.
	if (
		input.from === null &&
		input.hasPersistentShell &&
		isShellAreaPath(input.to)
	) {
		return false
	}
	return true
}

function swapDom(onSwapped?: () => void) {
	lastNotifiedDocumentPath = getCurrentDocumentPath()
	// `<body>` is outside `#root`, so SPA navigations must add or remove
	// 1Password's page ignore as the route changes.
	syncPasswordManagerPageIgnore(window.location.pathname)
	// First SPA swap onward: suppresses the [data-rise] page-open
	// choreography (public/styles.css) — the view transition is the
	// entrance for SPA navigations.
	document.documentElement.setAttribute('data-spa-nav', '')
	routerEvents.dispatchEvent(new Event('navigate'))
	// Subscribers only enqueue handle.update(); the remix/component scheduler
	// flushes in a microtask. Resolve one microtask later so the DOM has
	// actually swapped before the transition captures the new state.
	return new Promise<void>((resolve) =>
		queueMicrotask(() => {
			// Resolve before notifying: `onSwapped` dispatches to arbitrary
			// subscribers, and a throwing one would otherwise leave this promise
			// forever pending — freezing the view transition on its old snapshot.
			// The reaction still runs after this microtask, so the transition
			// captures the swapped DOM either way.
			resolve()
			onSwapped?.()
		}),
	)
}

function resolveViewTransitionStarter(): ViewTransitionStarter | null {
	if (typeof document === 'undefined') return null
	if (!('startViewTransition' in document)) return null
	return (
		document as Document & {
			startViewTransition: ViewTransitionStarter
		}
	).startViewTransition.bind(document)
}

const viewTransitionScheduler = createViewTransitionScheduler(
	resolveViewTransitionStarter,
)

function notify(onSwapped?: () => void) {
	const to = getCurrentDocumentPath()
	if (
		!shouldUseViewTransition({
			from: lastNotifiedDocumentPath,
			to,
			canStart: Boolean(resolveViewTransitionStarter()),
			prefersReducedMotion: matchMedia('(prefers-reduced-motion: reduce)')
				.matches,
			hasPersistentShell: documentHasPersistentShell(),
		})
	) {
		// Instant path: drop any in-flight/queued VT so a superseded chain
		// step cannot restart a transition after this swap.
		viewTransitionScheduler.cancelPending()
		void swapDom(onSwapped)
		return
	}
	// Serialize superseding navigations: skip the active animation, then wait
	// for its update callback to settle before starting the next transition
	// (Chrome InvalidStateError / unhandledrejection — KODY-CLOUDFLARE-3Y).
	viewTransitionScheduler.run(() => swapDom(onSwapped))
}

function createNavigationEventDetail(
	location: string,
	options?: NavigationRunOptions,
): RouterNavigationEventDetail {
	return {
		location,
		historyAction:
			options?.historyAction ??
			(options?.replace || options?.skipPushState ? 'replace' : 'push'),
		preventScrollReset: options?.preventScrollReset ?? false,
	}
}

function dispatchNavigationStart(options?: NavigationRunOptions) {
	routerEvents.dispatchEvent(
		new CustomEvent<RouterNavigationEventDetail>('navigationstart', {
			detail: createNavigationEventDetail(
				getCurrentPathWithSearchAndHash(),
				options,
			),
		}),
	)
}

function dispatchNavigationEnd(detail: RouterNavigationEventDetail) {
	routerEvents.dispatchEvent(
		new CustomEvent<RouterNavigationEventDetail>('navigationend', {
			detail,
		}),
	)
}

function createRouteMatcher(routes: Record<string, JSX.Element>) {
	const matcher = createMultiMatcher<JSX.Element>()
	for (const [pattern, routeElement] of Object.entries(routes)) {
		matcher.add(pattern, routeElement)
	}
	return matcher
}

function createLoaderMatcher(loaders: Record<string, RouteLoader>) {
	const matcher = createMultiMatcher<RouteLoader>()
	for (const [pattern, loader] of Object.entries(loaders)) {
		matcher.add(pattern, loader)
	}
	return matcher
}

function getRouteMatcher(routes: Record<string, JSX.Element>) {
	const existing = routeMatchers.get(routes)
	if (existing) return existing
	const matcher = createRouteMatcher(routes)
	routeMatchers.set(routes, matcher)
	return matcher
}

function getLoaderMatcher(loaders: Record<string, RouteLoader>) {
	const existing = loaderMatchers.get(loaders)
	if (existing) return existing
	const matcher = createLoaderMatcher(loaders)
	loaderMatchers.set(loaders, matcher)
	return matcher
}

export function registerRouteLoaders(loaders: Record<string, RouteLoader>) {
	registeredRouteLoaders = loaders
	loaderMatchers.delete(loaders)
}

let registeredClientRoutes: Record<string, JSX.Element> = {}
let clientRoutesRegistered = false

/**
 * Page-route registry used to decide whether a same-origin click should stay
 * in the SPA. Companion documents (`/docs/:slug.md`, `/blog/rss.xml`) are
 * not registered here — the worker serves those as raw responses.
 */
export function registerClientRoutes(routes: Record<string, JSX.Element>) {
	registeredClientRoutes = routes
	clientRoutesRegistered = Object.keys(routes).length > 0
	routeMatchers.delete(routes)
}

/**
 * True when this pathname is not a client-rendered page, so the browser
 * should leave the SPA (markdown twins, JSON companions, RSS, 404s the
 * worker owns). With no registry yet, assume every path is in-app so unit
 * tests that never register routes keep the historical intercept behavior.
 */
export function shouldLeaveDocumentForPath(pathname: string) {
	if (!clientRoutesRegistered) return false
	return matchRoute(pathname, registeredClientRoutes) === null
}

export function matchRouteLoader(
	path: string | URL,
	loaders: Record<string, RouteLoader> = registeredRouteLoaders,
): RouteLoader | null {
	const url = typeof path === 'string' ? new URL(path, clientRouteOrigin) : path
	return getLoaderMatcher(loaders).match(url)?.data ?? null
}

export function matchRoute(
	path: string,
	routes: Record<string, JSX.Element>,
): JSX.Element | null {
	return (
		getRouteMatcher(routes).match(new URL(path, clientRouteOrigin))?.data ??
		null
	)
}

/**
 * Remix frame navigation attributes. The client router must not preventDefault
 * these so the Navigation API can reload a named frame or force a document
 * submit.
 */
export function hasRemixFrameNavigationAttribute(element: Element) {
	return (
		element.hasAttribute('data-rmx-target') ||
		element.hasAttribute('data-rmx-src') ||
		element.hasAttribute('data-rmx-document')
	)
}

/**
 * True when the router will intercept this anchor click and run an SPA
 * navigation (plain left-click on a same-origin, non-download, self-target
 * link). Components that reset local state before an in-page navigation use
 * this so modified clicks (open in new tab, download) leave the current page
 * untouched.
 */
export function shouldRouterHandleClick(
	event: MouseEvent,
	anchor: HTMLAnchorElement,
) {
	if (event.defaultPrevented) return false
	if (event.button !== 0) return false
	if (event.metaKey || event.altKey || event.ctrlKey || event.shiftKey)
		return false
	if (anchor.target && anchor.target !== '_self') return false
	if (anchor.hasAttribute('download')) return false
	if (hasRemixFrameNavigationAttribute(anchor)) return false

	const href = anchor.getAttribute('href')
	if (!href) return false

	const destination = new URL(href, window.location.href)
	if (destination.origin !== window.location.origin) return false
	if (shouldLeaveDocumentForPath(destination.pathname)) return false
	return true
}

function handleDocumentClick(event: MouseEvent) {
	const target = event.target as Element | null
	const anchor = target?.closest('a') as HTMLAnchorElement | null
	if (!anchor || typeof window === 'undefined') return
	if (!shouldRouterHandleClick(event, anchor)) return

	event.preventDefault()
	const destination = new URL(anchor.href, window.location.href)
	navigate(`${destination.pathname}${destination.search}${destination.hash}`, {
		preventScrollReset: anchor.hasAttribute('data-prevent-scroll-reset'),
	})
}

type PrefetchableLink = {
	anchor: HTMLAnchorElement
	destination: URL
}

function getPrefetchableLink(
	target: EventTarget | null,
): PrefetchableLink | null {
	if (!(target instanceof Element)) return null
	const anchor = target.closest('a')
	if (!anchor || typeof window === 'undefined') return null
	if (anchor.dataset.prefetch === 'none') return null
	if (anchor.target && anchor.target !== '_self') return null
	if (anchor.hasAttribute('download')) return null

	const href = anchor.getAttribute('href')
	if (!href || href.startsWith('#')) return null

	const destination = new URL(href, window.location.href)
	if (destination.origin !== window.location.origin) return null
	if (shouldLeaveDocumentForPath(destination.pathname)) return null
	if (
		`${destination.pathname}${destination.search}` ===
		`${window.location.pathname}${window.location.search}`
	) {
		return null
	}
	return { anchor, destination }
}

function runIntentPrefetch(destination: URL) {
	const destinationPath = getPathWithSearchAndHashFromUrl(destination)
	// Never speculatively refetch the page the user is already on, or the one
	// a navigation is already loading (clicking a link focuses it, and that
	// focusin lands after the navigation consumed the prefetch slot).
	if (destinationPath === getCurrentPathWithSearchAndHash()) return
	if (destinationPath === activeNavigationPath) return
	if (
		isProfilePackageFilterOnlyHrefChange(
			getCurrentPathWithSearchAndHash(),
			destinationPath,
		)
	) {
		return
	}
	// Warm the destination's lazy code chunk too — loaders in lazy areas pull
	// their own chunk, but loaderless lazy routes (e.g. /connect/oauth) would
	// otherwise wait for the chunk at navigation time.
	void preloadClientRouteModules(
		`${destination.pathname}${destination.search}`,
	).catch(() => {
		// Speculative; navigation handles real failures.
	})
	const loader = matchRouteLoader(destination)
	if (!loader) return
	prefetchRouteOnIntent(
		getPathWithSearchAndHashFromUrl(destination),
		loader,
		destination,
	)
}

/**
 * Render prefetch for a list of same-origin hrefs. Destinations that share a
 * loader share one request unless `independent` is set (docs slugs share a
 * matcher, not a payload). Remix 3 in this repo has no `<Link prefetch>` —
 * this is the client-router equivalent of `prefetch="render"`.
 */
export function prefetchRouteHrefs(
	hrefs: ReadonlyArray<string>,
	options?: { independent?: boolean },
): void {
	const groups = new Map<RouteLoader, Array<string>>()
	const seen = new Set<string>()
	const currentPath = getCurrentPathWithSearchAndHash()

	for (const href of hrefs) {
		const destination = new URL(href, prefetchBaseHref())
		if (
			typeof window !== 'undefined' &&
			destination.origin !== window.location.origin
		) {
			continue
		}
		const destinationPath = getPathWithSearchAndHashFromUrl(destination)
		if (seen.has(destinationPath)) continue
		seen.add(destinationPath)
		if (destinationPath === currentPath) continue
		if (destinationPath === activeNavigationPath) continue
		if (isProfilePackageFilterOnlyHrefChange(currentPath, destinationPath)) {
			continue
		}
		void preloadClientRouteModules(
			`${destination.pathname}${destination.search}`,
		).catch(() => {
			// Speculative; navigation handles real failures.
		})
		const loader = matchRouteLoader(destination)
		if (!loader) continue
		const group = groups.get(loader) ?? []
		group.push(destinationPath)
		groups.set(loader, group)
	}

	const warm = options?.independent
		? prefetchEachRouteOnRender
		: prefetchRoutesOnRender
	for (const [loader, groupHrefs] of groups) {
		warm(groupHrefs, loader, (href) => new URL(href, prefetchBaseHref()))
	}
}

function prefetchBaseHref() {
	if (typeof window !== 'undefined' && window.location?.href) {
		return window.location.href
	}
	return clientRouteOrigin
}

/**
 * Hovers shorter than this are treated as the mouse passing through, not
 * intent to navigate, so sweeping across a nav list does not fire a
 * speculative request per link crossed.
 */
const hoverIntentDelayMs = 100

let hoverIntentAnchor: HTMLAnchorElement | null = null
let hoverIntentTimer: ReturnType<typeof setTimeout> | null = null

function cancelHoverIntent() {
	if (hoverIntentTimer !== null) {
		clearTimeout(hoverIntentTimer)
		hoverIntentTimer = null
	}
	hoverIntentAnchor = null
}

/**
 * Intent prefetch (hover / focus / touch on internal links, like React
 * Router's `prefetch="intent"`): speculatively runs the destination's route
 * loader so the data is already in flight — or already here — when the click
 * lands. Opt out per link with `data-prefetch="none"`.
 */
function handleIntentHoverStart(event: MouseEvent) {
	const link = getPrefetchableLink(event.target)
	if (!link) return
	if (hoverIntentAnchor === link.anchor) return

	cancelHoverIntent()
	hoverIntentAnchor = link.anchor
	hoverIntentTimer = setTimeout(() => {
		hoverIntentTimer = null
		hoverIntentAnchor = null
		runIntentPrefetch(link.destination)
	}, hoverIntentDelayMs)
}

function handleIntentHoverEnd(event: MouseEvent) {
	if (!hoverIntentAnchor) return
	if (
		!(event.target instanceof Node) ||
		!hoverIntentAnchor.contains(event.target)
	) {
		return
	}
	// mouseout between an anchor's children stays "hovering"; only cancel
	// when the pointer actually leaves the anchor.
	if (
		event.relatedTarget instanceof Node &&
		hoverIntentAnchor.contains(event.relatedTarget)
	) {
		return
	}
	cancelHoverIntent()
}

/** Focus and touch are deliberate; prefetch immediately without the delay. */
function handleImmediateIntent(event: Event) {
	const link = getPrefetchableLink(event.target)
	if (!link) return
	// A pending hover timer (possibly for a different link) must not fire
	// after this deliberate intent and abort its prefetch — the slot is
	// latest-wins and this is the latest intent.
	cancelHoverIntent()
	runIntentPrefetch(link.destination)
}

function getFormSubmitter(event: SubmitEvent) {
	const submitter = event.submitter
	if (
		submitter instanceof HTMLButtonElement ||
		submitter instanceof HTMLInputElement
	) {
		return submitter
	}
	return null
}

function normalizeFormMethod(rawMethod: string | null): FormMethod | null {
	const method = (rawMethod ?? 'get').trim().toLowerCase()
	if (method === 'get' || method === 'post') return method
	return null
}

function normalizeTarget(rawTarget: string | null) {
	return (rawTarget ?? '').trim().toLowerCase()
}

function createSubmitFormData(
	form: HTMLFormElement,
	submitter: HTMLButtonElement | HTMLInputElement | null,
) {
	return submitter ? new FormData(form, submitter) : new FormData(form)
}

function resolveFormSubmitDetails(
	form: HTMLFormElement,
	submitter: HTMLButtonElement | HTMLInputElement | null,
): FormSubmitDetails | null {
	const method = normalizeFormMethod(
		submitter?.getAttribute('formmethod') ?? form.getAttribute('method'),
	)
	if (!method) return null

	const target = normalizeTarget(
		submitter?.getAttribute('formtarget') ?? form.getAttribute('target'),
	)
	if (target && target !== '_self') return null

	const rawAction =
		submitter?.getAttribute('formaction') ?? form.getAttribute('action')
	const action = new URL(
		rawAction || window.location.href,
		window.location.href,
	)
	if (action.origin !== window.location.origin) return null

	const enctype = (
		submitter?.getAttribute('formenctype') ??
		form.getAttribute('enctype') ??
		'application/x-www-form-urlencoded'
	)
		.trim()
		.toLowerCase()

	return {
		action,
		method,
		enctype,
		formData: createSubmitFormData(form, submitter),
		preventScrollReset:
			form.hasAttribute('data-prevent-scroll-reset') ||
			submitter?.hasAttribute('data-prevent-scroll-reset') === true,
	}
}

function formDataToSearchParams(formData: FormData) {
	const params = new URLSearchParams()
	for (const [name, value] of formData.entries()) {
		params.append(name, getFormDataValueText(value))
	}
	return params
}

function formDataToPlainText(formData: FormData) {
	const lines: Array<string> = []
	for (const [name, value] of formData.entries()) {
		lines.push(`${name}=${getFormDataValueText(value)}`)
	}
	return lines.join('\r\n')
}

function getFormDataValueText(value: FormDataEntryValue) {
	if (typeof value === 'string') return value
	const fileName = (value as { name?: unknown }).name
	return typeof fileName === 'string' ? fileName : 'blob'
}

function buildGetDestination(action: URL, formData: FormData) {
	const destination = new URL(action.toString())
	destination.search = formDataToSearchParams(formData).toString()
	return destination
}

function getPathWithSearchAndHashFromUrl(url: URL) {
	return `${url.pathname}${url.search}${url.hash}`
}

function getCurrentPathWithSearchAndHash() {
	if (typeof window === 'undefined') return '/'
	return `${window.location.pathname}${window.location.search}${window.location.hash}`
}

function shouldReplaceHistory(options?: NavigationRunOptions) {
	return options?.replace === true || options?.historyAction === 'replace'
}

function commitNavigation(nextPath: string, onSwapped?: () => void) {
	window.history.pushState(
		createScrollRestorationHistoryState(window.history.state),
		'',
		nextPath,
	)
	notify(onSwapped)
}

function commitReplaceNavigation(nextPath: string, onSwapped?: () => void) {
	window.history.replaceState(
		createScrollRestorationHistoryState(window.history.state),
		'',
		nextPath,
	)
	notify(onSwapped)
}

function commitHistory(
	nextPath: string,
	options?: NavigationRunOptions,
	onSwapped?: () => void,
) {
	if (options?.skipPushState) {
		notify(onSwapped)
		return
	}
	if (shouldReplaceHistory(options)) {
		commitReplaceNavigation(nextPath, onSwapped)
		return
	}
	commitNavigation(nextPath, onSwapped)
}

function commitImmediateNavigation(
	nextPath: string,
	options?: NavigationRunOptions,
) {
	cancelHoverIntent()
	// A pending loader navigation must not commit after this immediate one
	// and clobber the URL we are about to push.
	navigationAbortController?.abort()
	navigationAbortController = null
	if (!options?.suppressStart) {
		dispatchNavigationStart(options)
	}
	commitNavigation(nextPath)
	dispatchNavigationEnd(createNavigationEventDetail(nextPath, options))
}

async function runNavigationWithLoader(
	destination: URL,
	options?: NavigationRunOptions,
) {
	// A hover-intent timer set right before the click (e.g. on mousedown)
	// must not fire after we navigate and prefetch the page we are already
	// heading to.
	cancelHoverIntent()
	navigationAbortController?.abort()
	const abortController = new AbortController()
	navigationAbortController = abortController
	const { signal } = abortController

	if (!options?.suppressStart) {
		dispatchNavigationStart(options)
	}

	const nextPath = getPathWithSearchAndHashFromUrl(destination)
	const navigationEndDetail = createNavigationEventDetail(nextPath, options)
	const loader = matchRouteLoader(destination)
	activeNavigationPath = nextPath
	const timeoutId = globalThis.setTimeout(() => {
		// A newer navigation aborts this one and owns recovery. Only the active
		// navigation may force the browser away from the current document.
		if (signal.aborted || navigationAbortController !== abortController) return
		abortController.abort()
		dispatchNavigationEnd({
			...navigationEndDetail,
			preventScrollReset: true,
		})
		window.location.assign(nextPath)
	}, navigationTimeoutMs)

	// Adopt an intent prefetch when one is pending or fresh for this
	// destination; otherwise run the loader now. Tying the prefetch to this
	// navigation's signal keeps latest-wins abort semantics. Taken
	// unconditionally (even for loaderless destinations) so a prefetch for a
	// link the user did not follow never outlives this navigation.
	const prefetched = takePrefetchedRouteResult(nextPath, signal)

	// Warm the destination lazy route chunk alongside the loader. A failed
	// chunk import is retried once (transient network blip), then falls back
	// to a full document navigation (e.g. hashed names rotated after a
	// deploy) instead of committing a broken SPA tree. The failure is
	// captured as a flag (never a rejection) so an unrelated loader error can
	// not leave this promise as an unhandled rejection.
	let chunkLoadFailed = false
	const destinationPathWithSearch = `${destination.pathname}${destination.search}`
	const preloadPromise = preloadClientRouteModules(destinationPathWithSearch)
		.catch(() => preloadClientRouteModules(destinationPathWithSearch))
		.catch((error: unknown) => {
			chunkLoadFailed = true
			console.error('Route chunk preload failed:', error)
		})

	try {
		let loadedData: Partial<AppLoaderData> | undefined
		if (loader) {
			const [, result] = await Promise.all([
				preloadPromise,
				prefetched ? prefetched : loader(destination, signal),
			])
			if (signal.aborted) return
			if (isRouteLoaderRedirect(result)) {
				// The loader wants a full-document navigation (e.g. a 401 →
				// login redirect). When the browser URL already moved
				// (popstate), still sync the UI to it so the app does not
				// render the previous route under the new URL while the full
				// document navigation loads.
				if (options?.skipPushState) {
					notify()
				}
				dispatchNavigationEnd({
					...navigationEndDetail,
					preventScrollReset: true,
				})
				window.location.assign(result.to)
				return
			}
			loadedData = result
		} else {
			await preloadPromise
		}

		if (signal.aborted) return

		if (chunkLoadFailed) {
			dispatchNavigationEnd({
				...navigationEndDetail,
				preventScrollReset: true,
			})
			window.location.assign(nextPath)
			return
		}

		// Store and commit in the same synchronous block so a superseding
		// navigation can never leave consume-once data behind for a URL the
		// user did not land on.
		if (loadedData) {
			setPreloadedNavigationData(nextPath, loadedData)
		}

		// Document head lives outside the hydrated `#root` tree, so SSR
		// `<title>` / OG / canonical tags would otherwise stick across SPA
		// navigations. Resolve from the shared registry (+ loader data for
		// dynamic routes) here so every route stays in sync without per-page
		// wiring.
		applyDocumentHead(destination.pathname, loadedData, destination.search)

		const finish = () => dispatchNavigationEnd(navigationEndDetail)
		commitHistory(nextPath, options, finish)
	} catch {
		// Promise.all rejects as soon as the loader fails, even if the lazy chunk
		// retry is still pending. Do not commit under the destination URL until
		// that retry settles: Router intentionally keeps the previous route
		// mounted while the chunk is cold, so committing early would also prevent
		// the destination LazyRoute from mounting and running its own recovery.
		await preloadPromise
		if (signal.aborted) return
		if (chunkLoadFailed) {
			// The loader also failed, but the missing chunk is what makes SPA
			// commit impossible — recover with a full document navigation.
			dispatchNavigationEnd({
				...navigationEndDetail,
				preventScrollReset: true,
			})
			window.location.assign(nextPath)
			return
		}
		// The loader failed, so no preloaded data exists for the committed
		// destination. Same-path refreshes (form POST redirects back to the
		// current URL) have no href change to trigger a route's fallback
		// refetch, so mark the destination stale for routes to consume.
		markNavigationDataStale(nextPath)
		applyDocumentHead(destination.pathname, undefined, destination.search)
		const finish = () => dispatchNavigationEnd(navigationEndDetail)
		commitHistory(nextPath, options, finish)
	} finally {
		globalThis.clearTimeout(timeoutId)
		// A superseding navigation owns the marker now; only clear our own.
		if (navigationAbortController === abortController) {
			activeNavigationPath = null
		}
	}
}

async function navigateWithRefreshForSamePath(
	destination: URL,
	options?: Pick<NavigationRunOptions, 'suppressStart' | 'preventScrollReset'>,
) {
	if (
		getPathWithSearchAndHashFromUrl(destination) ===
		getCurrentPathWithSearchAndHash()
	) {
		await runNavigationWithLoader(new URL(window.location.href), {
			historyAction: 'replace',
			preventScrollReset: options?.preventScrollReset ?? false,
			skipPushState: true,
			suppressStart: options?.suppressStart,
		})
		return
	}
	await navigateInternal(destination.toString(), options)
}

async function submitFormThroughRouter(details: FormSubmitDetails) {
	if (details.method === 'get') {
		navigate(buildGetDestination(details.action, details.formData).toString(), {
			preventScrollReset: details.preventScrollReset,
		})
		return
	}

	// The POST is about to mutate server state, so any speculative loader data
	// fetched before the mutation must never be adopted by the follow-up
	// navigation — including a hover timer that has not fired yet.
	cancelHoverIntent()
	abortIntentPrefetch()
	clearOnboardingPayloadCache()

	// Participate in the latest-wins navigation chain: a newer navigation
	// aborts this submission's follow-up redirect navigation so a late
	// response cannot hijack the URL. The POST itself is a mutation and is
	// never cancelled client-side.
	navigationAbortController?.abort()
	const abortController = new AbortController()
	navigationAbortController = abortController
	const { signal } = abortController

	// One `navigationstart` covers the POST fetch and the follow-up loader run;
	// `navigateWithRefreshForSamePath` / `navigateInternal` suppress a second
	// start and own the matching `navigationend`.
	dispatchNavigationStart({
		preventScrollReset: details.preventScrollReset,
	})

	try {
		const init: RequestInit = {
			method: details.method.toUpperCase(),
			credentials: 'include',
			redirect: 'follow',
		}

		if (details.enctype === 'application/x-www-form-urlencoded') {
			init.body = formDataToSearchParams(details.formData)
			init.headers = {
				'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
			}
		} else if (details.enctype === 'text/plain') {
			init.body = formDataToPlainText(details.formData)
			init.headers = {
				'Content-Type': 'text/plain;charset=UTF-8',
			}
		} else {
			init.body = details.formData
		}

		const response = await fetch(details.action.toString(), init)
		// The server processed the mutation no matter which navigation follows
		// (even if a newer navigation aborted this one). Tell listeners that
		// server-derived state they cache — like the shell's session info —
		// may now be stale.
		routerEvents.dispatchEvent(new Event('mutation'))
		if (signal.aborted) return

		if (response.redirected) {
			await navigateWithRefreshForSamePath(
				new URL(response.url, window.location.href),
				{
					preventScrollReset: details.preventScrollReset,
					suppressStart: true,
				},
			)
			return
		}

		const location = response.headers.get('Location')
		if (location) {
			await navigateWithRefreshForSamePath(new URL(location, details.action), {
				preventScrollReset: details.preventScrollReset,
				suppressStart: true,
			})
			return
		}

		throw new Error(
			`Expected redirect location after form submit (${response.status} ${response.statusText})`,
		)
	} catch (error: unknown) {
		console.error('Router form submit failed', error)
		if (signal.aborted) return
		dispatchNavigationEnd(
			createNavigationEventDetail(getCurrentPathWithSearchAndHash(), {
				historyAction: 'replace',
				preventScrollReset: true,
			}),
		)
	}
}

function handleDocumentSubmit(event: Event) {
	if (!(event instanceof SubmitEvent)) return
	if (typeof window === 'undefined') return
	if (event.defaultPrevented) return
	if (!(event.target instanceof HTMLFormElement)) return
	if (event.target.hasAttribute('data-router-skip')) return
	if (hasRemixFrameNavigationAttribute(event.target)) return

	const submitter = getFormSubmitter(event)
	if (submitter && hasRemixFrameNavigationAttribute(submitter)) return
	const details = resolveFormSubmitDetails(event.target, submitter)
	if (!details) return

	event.preventDefault()
	void submitFormThroughRouter(details)
}

function handlePopState() {
	// Hash-only history moves have no data to load — mirror forward hash-only
	// navigations (`commitImmediateNavigation`) and sync the UI immediately
	// instead of running the loader.
	if (getCurrentDocumentPath() === lastNotifiedDocumentPath) {
		cancelHoverIntent()
		// A pending loader navigation must not commit after this immediate
		// one and clobber the URL the browser already restored.
		navigationAbortController?.abort()
		navigationAbortController = null
		dispatchNavigationStart({ historyAction: 'pop' })
		notify()
		dispatchNavigationEnd(
			createNavigationEventDetail(getCurrentPathWithSearchAndHash(), {
				historyAction: 'pop',
			}),
		)
		return
	}
	if (
		lastNotifiedDocumentPath &&
		isProfilePackageFilterOnlyHrefChange(
			lastNotifiedDocumentPath,
			getCurrentDocumentPath(),
		)
	) {
		cancelHoverIntent()
		navigationAbortController?.abort()
		navigationAbortController = null
		dispatchNavigationStart({ historyAction: 'pop' })
		notify()
		dispatchNavigationEnd(
			createNavigationEventDetail(getCurrentPathWithSearchAndHash(), {
				historyAction: 'pop',
				preventScrollReset: true,
			}),
		)
		return
	}
	void runNavigationWithLoader(new URL(window.location.href), {
		historyAction: 'pop',
		skipPushState: true,
	})
}

function ensureRouter() {
	if (typeof document === 'undefined') return
	if (routerInitialized) return
	routerInitialized = true
	ensureCurrentScrollRestorationKey()
	lastNotifiedDocumentPath = getCurrentDocumentPath()
	syncPasswordManagerPageIgnore(window.location.pathname)
	window.addEventListener('popstate', handlePopState)
	document.addEventListener('click', handleDocumentClick)
	document.addEventListener('submit', handleDocumentSubmit)
	document.addEventListener('mouseover', handleIntentHoverStart)
	document.addEventListener('mouseout', handleIntentHoverEnd)
	document.addEventListener('focusin', handleImmediateIntent)
	document.addEventListener('touchstart', handleImmediateIntent, {
		passive: true,
	})
	installFileDropNavigationGuard()
}

export function listenToRouterNavigation(
	handle: Pick<Handle, 'signal' | 'update'>,
	listener: () => void,
) {
	if (typeof document === 'undefined') return
	ensureRouter()
	routerEvents.addEventListener('navigate', () => listener(), {
		signal: handle.signal,
	})
}

export function listenToRouterNavigationEnd(
	handle: Pick<Handle, 'signal' | 'update'>,
	listener: (detail: RouterNavigationEventDetail) => void,
) {
	if (typeof document === 'undefined') return
	ensureRouter()
	routerEvents.addEventListener(
		'navigationend',
		(event) => {
			listener((event as CustomEvent<RouterNavigationEventDetail>).detail)
		},
		{ signal: handle.signal },
	)
}

/**
 * Fires after the router submits a non-GET form (a mutation, e.g. logout).
 * Listeners that cache or throttle server-derived state must revalidate:
 * the follow-up redirect is a SPA navigation, so nothing else re-fetches
 * state the mutation may have changed.
 */
export function listenToRouterMutations(
	handle: Pick<Handle, 'signal' | 'update'>,
	listener: () => void,
) {
	if (typeof document === 'undefined') return
	ensureRouter()
	routerEvents.addEventListener('mutation', () => listener(), {
		signal: handle.signal,
	})
}

export function getPathname(handle?: Pick<Handle, 'context'>) {
	if (handle) {
		try {
			return readRouterPathname(handle as Handle)
		} catch {
			// Router location context is unavailable outside the app tree.
		}
	}
	if (typeof window === 'undefined') return '/'
	return window.location.pathname
}

async function navigateInternal(to: string, options?: NavigationRunOptions) {
	const destination = new URL(to, window.location.href)
	if (destination.origin !== window.location.origin) {
		window.location.assign(destination.toString())
		return
	}
	if (shouldLeaveDocumentForPath(destination.pathname)) {
		window.location.assign(getPathWithSearchAndHashFromUrl(destination))
		return
	}

	const current = new URL(window.location.href)
	const nextPath = getPathWithSearchAndHashFromUrl(destination)
	const currentPath = getCurrentPathWithSearchAndHash()

	if (nextPath === currentPath) {
		// Re-activating the current hash (hero CTA while
		// already at `/#invite`) must still scroll to the target. Native
		// fragment clicks do that; a no-op here would leave the viewport stuck.
		if (destination.hash) {
			dispatchNavigationEnd(
				createNavigationEventDetail(nextPath, {
					...options,
					historyAction: 'replace',
					skipPushState: true,
				}),
			)
		}
		return
	}

	const sameDocumentLocation =
		`${destination.pathname}${destination.search}` ===
		`${current.pathname}${current.search}`
	if (sameDocumentLocation && destination.hash !== current.hash) {
		commitImmediateNavigation(nextPath, options)
		return
	}

	if (isProfilePackageFilterOnlyHrefChange(currentPath, nextPath)) {
		commitImmediateNavigation(nextPath, {
			...options,
			preventScrollReset: options?.preventScrollReset ?? true,
		})
		return
	}

	await runNavigationWithLoader(destination, options)
}

export function navigate(to: string, options?: RouterNavigateOptions): void {
	if (typeof window === 'undefined') return
	void navigateInternal(to, options).catch(() => {
		// Fire-and-forget: existing callers must not observe rejections.
	})
}

type RouterHandle = Pick<Handle, 'signal' | 'update' | 'context'> & {
	props: RouterSetup
}

export function Router(handle: RouterHandle) {
	let renderedRouteElement: JSX.Element | null = null

	if (typeof document !== 'undefined') {
		listenToRouterNavigation(handle, () => {
			void handle.update()
		})
		listenToLazyRouteLoads(handle, () => {
			void handle.update()
		})
	}

	return () => {
		// The server's 404 verdict only applies to the URL it rendered;
		// after SPA navigation, match routes normally again.
		if (handle.props.unauthorized && isOnSsrUrl(handle)) {
			return handle.props.unauthorizedFallback ?? handle.props.fallback ?? null
		}
		if (handle.props.internalError && isOnSsrUrl(handle)) {
			return handle.props.internalErrorFallback ?? handle.props.fallback ?? null
		}
		if (handle.props.notFound && isOnSsrUrl(handle)) {
			return handle.props.fallback ?? null
		}

		const path = readRouterPathname(handle)
		const routeElement = matchRoute(path, handle.props.routes)
		if (routeElement) {
			// Navigation normally preloads lazy areas before commit. If a loader
			// fails early or another edge case commits first, keep the previous
			// route mounted until the destination chunk reports that it is ready.
			if (
				renderedRouteElement &&
				!isClientRouteModuleCached(readRouterUrl(handle))
			) {
				return renderedRouteElement
			}
			renderedRouteElement = routeElement
			return routeElement
		}
		renderedRouteElement = null
		return handle.props.fallback ?? null
	}
}

function normalizeHref(href: string) {
	const url = new URL(href, clientRouteOrigin)
	return `${url.pathname}${url.search}${url.hash}`
}

export function isOnSsrUrl(handle: Pick<Handle, 'context'>) {
	return (
		normalizeHref(readRouterUrl(handle)) ===
		normalizeHref(readSsrRouterUrl(handle))
	)
}

export function readCurrentRouterHref(handle: Handle) {
	return readRouterUrl(handle)
}
